<template>
  <div v-loading="pageLoading" class="app-container production-planning-series">
    <el-row class="page-buttons">
      <label style="font-size:1.4em">{{ title }}</label>
      <editor-button action="cancel" style="float:right" @click="close" />
    </el-row>
    <div style="clear:both;">
      <div style="float:left;padding-bottom:5px">
        <div style="margin-bottom: 5px">
          <label>{{ $t('series.addOrderNumber') }}</label>
        </div>
        <el-input
          ref="saveTagInput"
          v-model="orderNumberToAdd"
          class="input-new-tag"
          size="mini"
          style="width:257px;padding-bottom:3px;"
          placeholder="LB1234567890/01(/01)"
          @keyup.enter.native="handleInputConfirm"
        >
        </el-input>
        <editor-button
          :icon-only="false"
          :disabled="pageLoading || !orderNumberToAdd"
          button-translation="common.add"
          action="primary"
          style="margin-top:3px"
          @click="addOrderNumbersToSeries()"
        />
      </div>
      <div style="float:right;padding-bottom:5px">
        <div style="margin-bottom: 5px">
          <label>{{ $t('series.bulkActionLabel') }}</label>
        </div>
        <div>
          <el-date-picker
            v-model="estimatedDeliveryDate"
            style="padding-right:5px"
            :format="dateFormat"
            value-format="yyyy-MM-dd"
            :placeholder="$t('orderLine.estimatedDeliveryDate')"
          />
          <el-select
            v-model="fromStatusID"
            style="padding-right:5px"
            type="select"
            :placeholder="$t('orderLine.fromStatus')"
            clearable
          >
            <el-option-group :label="$t('orderLine.status')">
              <el-option
                v-for="option in this.$store.getters['orderLineStatus/loadedItems'].filter(
                  s => s.settableYN || statusesInSeries.includes(s.id)
                )"
                :key="option.id"
                :label="option.name"
                :value="option.id"
              />
            </el-option-group>
          </el-select>
          <el-select v-model="toStatusID" type="select" :placeholder="$t('orderLine.toStatus')" clearable>
            <el-option-group :label="$t('orderLine.status')">
              <el-option
                v-for="option in this.$store.getters['orderLineStatus/loadedItems'].filter(s => s.settableYN)"
                :key="option.id"
                :label="option.name"
                :value="option.id"
              />
            </el-option-group>
          </el-select>
          <editor-button
            :icon-only="false"
            :disabled="pageLoading || !hasItemsSelected || (!toStatusID && !estimatedDeliveryDate)"
            button-translation="series.updateOrderLines"
            action="primary"
            @click="updateSeriesOrderLineStatusesWithStatusAfterConfirmation()"
          />
          <editor-button
            :icon-only="false"
            :disabled="pageLoading || !hasItemsSelected"
            button-translation="series.printTickets"
            action="primary"
            @click="printSelectedOrderLinesAfterConfirmation()"
          />
          <editor-button
            :icon-only="false"
            :disabled="pageLoading || !hasItemsSelected"
            button-translation="common.deletePlural"
            action="delete"
            @click="deleteSelectedOrderLinesFromSeriesAfterConfirmation()"
          />
        </div>
      </div>
    </div>
    <select-printer-client-dialog
      :dialog-visible.sync="printDialogVisible"
      :on-select="printerClientSelected"
      :on-cancel="printerClientSelectionCancelled"
    />
    <el-table
      ref="dialogOrderList"
      :data="filteredItems"
      border
      fit
      highlight-current-row
      style="width: 100%;"
      height="700"
      @row-dblclick="rowDblClick"
      @selection-change="handleSelectionChange"
      @sort-change="sortChange"
    >
      <el-table-column :label="$t('orderLine.ticketNumber')" property="orderNumber" width="150">
        <template slot-scope="scope">
          <span>{{ scope.row['ticketNumber'] }}</span>
        </template>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="consumerSearch"
            class="search"
            size="mini"
            :placeholder="$t('orderLine.consumerName')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('orderLine.consumerName')" property="consumerName">
          <template slot-scope="scope">
            <span>{{ scope.row['consumerName'] }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-model="fabricSearch"
            class="search"
            size="mini"
            :placeholder="$t('orderLine.fabricName')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column :label="$t('orderLine.fabricName')" property="fabricDescription" min-width="100" sortable>
          <template slot-scope="scope">
            <span>{{ scope.row['fabricDescription'] }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('orderLine.liningFabricProduct')"
          property="liningFabricDescription"
          min-width="100"
        >
          <template slot-scope="scope">
            <span>{{ scope.row['liningFabricDescription'] }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column :label="$t('orderLine.orderDate')" property="orderedAt" width="150">
        <template slot-scope="scope">
          <span><date-time-picker v-model="scope.row['orderedAt']" :format="dateTimeFormat" disabled/></span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.estimatedDeliveryDate')" property="estimatedDeliveryDate" width="150">
        <template slot-scope="scope">
          <span><date-time-picker v-model="scope.row['estimatedDeliveryDate']" :format="dateFormat" disabled/></span>
        </template>
      </el-table-column>
      <el-table-column :label="amountString" property="totalCapacity" width="100px">
        <template slot-scope="scope">
          <span>{{ scope.row['totalCapacity'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('fabric.yarn')" property="yarnDescription" width="60px">
        <template slot-scope="scope">
          <span>{{ scope.row['yarnDescription'] }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.urgent')" property="urgentYN" width="100px">
        <template slot-scope="scope">
          <span>{{ scope.row['urgentYN'] ? $t('common.yes') : $t('common.no') }}</span>
        </template>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-select v-model="sampleSearch" class="search-select" size="mini" clearable @change="handleSampleFilter">
            <el-option
              v-for="option in [
                { value: true, label: $t('common.yes') },
                { value: false, label: $t('common.no') }
              ]"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </template>
        <el-table-column :label="$t('orderLine.show')" property="urgentYN" width="100px">
          <template slot-scope="scope">
            <span>{{ scope.row['sampleYN'] ? $t('common.yes') : $t('common.no') }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-select
            v-model="orderLineStatusSearch"
            class="search-select"
            size="mini"
            :placeholder="$t('table.searchSelectPlaceholder')"
            clearable
            @change="handleStatusChange"
          >
            <el-option
              v-for="statusName in substatusesInSeries"
              :key="statusName"
              :label="statusName"
              :value="statusName"
            />
          </el-select>
        </template>
        <el-table-column :label="$t('orderLine.status')" property="orderLineSubstatusDescription" width="150">
          <template slot-scope="scope">
            <span>{{ scope.row['orderLineSubstatusDescription'] }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column :label="$t('orderLine.printStatusOption')" property="printStatusOption" width="180px">
        <template slot-scope="scope">
          <span>
            <el-alert
              v-if="showPrintStatus(scope.row)"
              :title="getPrintStatusOptionText(scope.row)"
              :type="getPrintStatusOptionAlertType(scope.row)"
              :closable="false"
              show-icon
            >
            </el-alert>
          </span>
        </template>
      </el-table-column>
      <el-table-column type="selection" width="40" align="center" :selectable="() => true"></el-table-column>
    </el-table>
  </div>
</template>

<script>
import { closeCurrentTag } from '@/utils/tags';
import DateTimePicker from '@/components/DSE/DateTimePicker';
import EditorButton from '@/components/crud/EditorButton';
import { PlanningOption, PrintStatusOption } from '@/utils/constants';
import SelectPrinterClientDialog from '@/pages/components/SelectPrinterClientDialog';
import { isGiven } from '@/utils';

const debounce = require('lodash.debounce');

export default {
  name: 'DialogOrderList',
  components: {
    EditorButton,
    DateTimePicker,
    SelectPrinterClientDialog
  },
  props: {
    entity: {
      default: '',
      type: String
    },
    permissionCode: String,
    planningOption: {
      type: Number
    }
  },
  data() {
    return {
      pageLoading: false,
      dialogVisible: true,
      amountString: '',
      items: [],
      filteredItems: [],
      multipleSelection: [],

      orderNumberToAdd: '',
      // order line status update
      fromStatusID: null,
      toStatusID: null,
      estimatedDeliveryDate: null,
      printDialogVisible: false,
      statusesInSeries: [],
      consumerSearch: '',
      fabricSearch: '',
      orderLineStatusSearch: '',
      sampleSearch: undefined,
      title: '',
      sort: undefined
    };
  },
  computed: {
    dateFormat() {
      return this.$store.getters.dateFormat;
    },
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    hasItemsSelected() {
      return !!this.multipleSelection.length;
    },
    substatusesInSeries() {
      const items = this.items;
      if (items) {
        return items
          .map(i => i.orderLineSubstatusDescription)
          .filter((value, index, self) => self.indexOf(value) === index);
      }
      return [];
    }
  },
  watch: {
    consumerSearch() {
      this.debouncedFilterOrders();
    },
    fabricSearch() {
      this.debouncedFilterOrders();
    }
  },
  async created() {
    await this.$store.dispatch('orderLineStatus/getItems');
    this.getOrdersInSeries();
    this.getRequiredAmountTranslation();
    this.debouncedFilterOrders = debounce(this.filterOrders, 1000);
  },
  methods: {
    sortChange({ column, prop, order }) {
      if (prop !== null) {
        this.sort = order && (order === 'descending' ? prop + '_desc' : prop);
      }
    },
    close() {
      closeCurrentTag(this);
    },
    filterOrders() {
      let filteredItems = [...this.items];
      if (this.consumerSearch && this.consumerSearch.length) {
        const capitalizedVal = this.consumerSearch.toUpperCase();
        filteredItems = filteredItems.filter(x => x.consumerName.toUpperCase().includes(capitalizedVal));
      }
      if (this.fabricSearch && this.fabricSearch.length) {
        const capitalizedVal = this.fabricSearch.toUpperCase();
        filteredItems = filteredItems.filter(
          x =>
            x.fabricDescription.toUpperCase().includes(capitalizedVal) ||
            (x.liningFabricDescription && x.liningFabricDescription.toUpperCase().includes(capitalizedVal))
        );
      }
      this.filteredItems = Object.freeze(filteredItems);
    },
    getOrdersInSeries() {
      this.pageLoading = true;
      this.$store
        .dispatch('productionPlanningSeries/getSeries', {
          seriesID: this.$route.params.id
        })
        .then(data => {
          const orderLines = Object.freeze(data.orderLines);
          this.items = orderLines;
          this.filteredItems = orderLines;
          this.statusesInSeries = orderLines.map(i => i.orderLineStatusID);
          this.title = data.seriesNumber + ', ' + data.seriesDescription;
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    handleSampleFilter(val) {
      if (isGiven(val)) {
        this.filteredItems = Object.freeze(this.items.filter(x => x.sampleYN === val));
      } else {
        this.filteredItems = this.items;
      }
    },
    handleStatusChange(val) {
      if (val) {
        this.filteredItems = Object.freeze(this.items.filter(x => x.orderLineSubstatusDescription === val));
      } else {
        this.filteredItems = this.items;
      }
    },
    updateSeriesOrderLineStatusesWithStatusAfterConfirmation() {
      this.$store.dispatch('notify/confirm', { message: this.$i18n.t('series.confirmUpdate') }).then(() => {
        this.updateSeriesOrderLineStatusesWithStatus();
      });
    },
    updateSeriesOrderLineStatusesWithStatus() {
      this.pageLoading = true;
      const orderIDs = this.multipleSelection.map(i => i.orderLineID);
      this.$store
        .dispatch('productionPlanningSeries/updateSeriesOrderLineStatusesWithStatus', {
          seriesID: this.$route.params.id,
          selection: orderIDs,
          fromStatusID: this.fromStatusID || null,
          toStatusID: this.toStatusID || null,
          estimatedDeliveryDate: this.estimatedDeliveryDate
        })
        .then(data => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_updated') });
          this.getOrdersInSeries();
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    deleteSelectedOrderLinesFromSeriesAfterConfirmation() {
      this.$store.dispatch('notify/confirm', { message: this.$i18n.t('series.confirmDelete') }).then(() => {
        this.deleteSelectedOrderLinesFromSeries();
      });
    },
    deleteSelectedOrderLinesFromSeries() {
      this.pageLoading = true;
      const orderIDs = this.multipleSelection.map(i => i.orderLineID);
      this.$store
        .dispatch('productionPlanningSeries/deleteSelectedOrderLinesFromSeries', {
          seriesID: this.$route.params.id,
          selection: orderIDs
        })
        .then(data => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_updated') });
          this.getOrdersInSeries();
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    async printSelectedOrderLinesAfterConfirmation() {
      this.$store.dispatch('notify/confirm', { message: this.$i18n.t('series.confirmPrint') }).then(() => {
        this.checkForAlreadyPrintedAndPrintAfterConfirmation();
      });
    },
    async printerClientSelected(printerClientID) {
      this.printDialogVisible = false;
      await this.doPrintSelectedOrderLines(printerClientID);
    },
    printerClientSelectionCancelled() {
      this.printDialogVisible = false;
    },
    async checkForAlreadyPrintedAndPrintAfterConfirmation() {
      const alreadyPrinted = this.multipleSelection.filter(
        i => i.printStatusOption === PrintStatusOption.Printed || i.printStatusOption === PrintStatusOption.Queued
      );
      if (alreadyPrinted.length > 0) {
        this.$store
          .dispatch('notify/confirm', {
            message: this.$i18n.t('series.ticketsAlreadyPrintedMessage', {
              printedTickets: alreadyPrinted.length,
              totalTickets: this.multipleSelection.length
            })
          })
          .then(() => {
            this.printDialogVisible = true;
          })
          .catch(() => {
            console.log('print cancelled by user');
          });
      } else {
        this.printDialogVisible = true;
      }
    },
    async doPrintSelectedOrderLines(printerClientID) {
      const orderIDs = this.multipleSelection.map(i => i.orderLineID);
      const printCommand = {
        seriesID: this.$route.params.id,
        printerClientID: printerClientID,
        overrideAlreadyPrinted: true,
        orderLines: orderIDs,
        sort: this.sort
      };
      this.$store.dispatch('notify/success', {
        message: this.$i18n.t('series.seriesPrintStart', { toBePrintedCount: orderIDs.length })
      });
      this.$store
        .dispatch('productionPlanningSeries/printTickets', printCommand)
        .then(result => {
          const printDestination =
            this.$store.getters['printerClient/getCurrentPrinterClientName'] ||
            this.$i18n.t('common.plannedWorkshopPrintClient');
          this.actionBusy = false;
          this.$store.dispatch('notify/success', {
            message: this.$i18n.t('series.seriesPrintSuccess', { printDestination })
          });
        })
        .catch(error => {
          console.error(error);
          this.actionBusy = false;
          this.$store.dispatch('notify/error', { message: this.$i18n.t('series.seriesPrintError') });
        });
    },
    rowDblClick(row) {
      this.$router.push({ name: 'EditOrderLine', params: { id: row.orderLineID } });
    },
    getRequiredAmountTranslation() {
      switch (this.planningOption) {
        case PlanningOption.Panel:
          this.amountString = this.$i18n.t('series.numberOfPanels');
          break;
        case PlanningOption.Cutting:
        case PlanningOption.NetCurtainRoomHigh:
        case PlanningOption.CurtainRoomHigh:
          this.amountString = this.$i18n.t('series.numberOfMeters');
          break;
        case PlanningOption.RomanBlind:
          this.amountString = this.$i18n.t('series.pieces');
          break;
      }
    },
    getPrintStatusOptionAlertType(row) {
      let alertType = 'info';
      switch (row.printStatusOption) {
        case PrintStatusOption.Printed:
          alertType = 'success';
          break;
        case PrintStatusOption.Failed:
          alertType = 'error';
          break;
        case PrintStatusOption.Queued:
          alertType = 'info';
          break;
      }
      return alertType;
    },
    getPrintStatusOptionText(row) {
      let printStatusOptionText = '';
      switch (row.printStatusOption) {
        case PrintStatusOption.Printed:
          printStatusOptionText = this.$i18n.t('orderLine.printStatusOptionPrinted');
          break;
        case PrintStatusOption.Failed:
          printStatusOptionText = this.$i18n.t('orderLine.printStatusOptionFailed');
          break;
        case PrintStatusOption.Queued:
          printStatusOptionText = this.$i18n.t('orderLine.printStatusOptionQueued');
          break;
      }
      return printStatusOptionText;
    },
    showPrintStatus(row) {
      return row.printStatusOption !== PrintStatusOption.Unattempted;
    },
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },
    selectAllItems(rows) {
      rows.forEach(row => this.$refs.dialogOrderList.toggleRowSelection(row, true));
    },
    clearSelection() {
      this.$refs.dialogOrderList.clearSelection();
    },
    handleInputConfirm() {
      console.log('enter');
    },
    addOrderNumbersToSeries() {
      this.pageLoading = true;
      this.$store
        .dispatch('productionPlanningSeries/addOrderNumberToSeries', {
          seriesID: this.$route.params.id,
          orderNumber: this.orderNumberToAdd
        })
        .then(result => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('series.orderNumbersAdded') });
          this.orderNumberToAdd = '';
          this.getOrdersInSeries();
        })
        .catch(error => {
          console.error(error);
          this.$store.dispatch('notify/error', { message: this.$i18n.t('series.orderNumbersNotAdded') });
        })
        .finally(() => {
          this.pageLoading = false;
        });
    }
  }
};
</script>
<style lang="scss">
.reset-button {
  background-color: #ff8c8c;
  color: #fff;
  margin-left: 0;
  margin-top: 5px;
  float: right;
}

.production-planning-series {
  .filter-cell > div {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .search {
    .el-input__inner {
      padding-left: 25px;
      padding-right: 20px;
    }
    .el-input__prefix {
      left: 0;
    }
    .el-input__suffix {
      right: 0;
    }
  }
  .search-select {
    width: 100%;
    .el-input__inner {
      padding-left: 5px;
    }
    .el-input {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
}
</style>
